import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, Button } from 'react-bootstrap';
import { useThemeMode } from '../../ThemeContext';
import LightChat from '../../assets/landing_page_images/Light-Chat.webp';
import DarkChat from '../../assets/landing_page_images/Dark-Chat.webp';
import SeoPageLayout from './SeoPageLayout';
import useSeoMeta from './useSeoMeta';
import { getPublicSeoPage } from '../../seo/publicPages';
import { APP_URLS } from '../../helpers/url_helper';
import './AIComplianceAssistant.css';

const HELP_USES = [
  {
    title: 'Ask questions from company documents',
    text: 'Find information in policies, procedures and registers using conversational questions.',
  },
  {
    title: 'Review compliance requirements',
    text: 'Cross-reference information with WHS legislation, ISO standards and government requirements.',
  },
  {
    title: 'Support audits',
    text: 'Use clause-cited answers to help connect audit findings with relevant requirements.',
  },
  {
    title: 'Draft compliance documents',
    text: 'Get drafting support for policies, procedures, risk assessments and SWMS.',
  },
  {
    title: 'Review site photos',
    text: 'Submit images for photo-based audit and site-condition prompts to help identify potential issues.',
  },
  {
    title: 'Work with uploaded files',
    text: 'Attach PDF, Word, Excel and PowerPoint files to provide additional context.',
  },
  {
    title: 'Keep conversations organised',
    text: 'Use project-scoped and history-based chat sessions for different compliance activities.',
  },
];

const USE_CASES = [
  {
    title: 'Find a Company Procedure',
    paragraphs: [
      'Instead of manually searching folders, a supervisor can ask a question such as:',
    ],
    quote: 'What is our fatigue management procedure?',
    afterQuote:
      "The assistant can use the company's uploaded documents to provide an answer based on available information.",
  },
  {
    title: 'Prepare for an Audit',
    paragraphs: [
      <>
        A WHS or quality manager can use the assistant to investigate audit requirements and connect findings with relevant clauses or compliance information. Learn more about{' '}
        <Link
          to={APP_URLS.WHS_COMPLIANCE_AUDIT_SOFTWARE}
          className="section-description-link"
        >
          WHS compliance and audit software
        </Link>
        .
      </>,
    ],
  },
  {
    title: 'Draft a Policy or SWMS',
    paragraphs: [
      <>
        The assistant can provide drafting support for policies, procedures, risk assessments and{' '}
        <Link
          to={APP_URLS.SWMS_PCBU_COMPLIANCE}
          className="section-description-link"
        >
          SWMS and PCBU compliance
        </Link>
        . The resulting content should be reviewed by an appropriately qualified person before being adopted as a business document. Complimate&apos;s own guidance specifically cautions against presenting AI-generated documents or recommendations as legal advice.
      </>,
    ],
  },
  {
    title: 'Review a Site Photo',
    paragraphs: [
      'A site supervisor can provide a photo for a compliance-focused review. The assistant can help identify potential risks, provide observations and support the process of creating follow-up actions.',
    ],
  },
];

const DOC_FEATURES = [
  { feature: 'Central document library', helps: 'Keep compliance documents in one location' },
  { feature: 'Folder hierarchy', helps: 'Organise documents by relevant categories' },
  { feature: 'Version management', helps: 'Track document versions' },
  { feature: 'Review dates', helps: 'Record when documents need review' },
  { feature: 'Expiry dates', helps: 'Track documents approaching expiry' },
  { feature: 'Search', helps: 'Find documents within the library' },
  { feature: 'Expiry filters', helps: 'Identify expired and soon-to-expire documents' },
  { feature: 'Document types', helps: 'Manage WHS, Quality, Environment and CoR documents' },
];

const SPEC_TABLE = [
  { capability: 'Product', value: 'AI Compliance Assistant' },
  { capability: 'Primary use', value: 'WHS and compliance assistance' },
  { capability: 'Document context', value: 'Company-uploaded documents' },
  { capability: 'Supported attachments', value: 'PDF, Word, Excel, PowerPoint' },
  {
    capability: 'Compliance references',
    value: 'WHS legislation, ISO and government requirements',
  },
  {
    capability: 'Drafting support',
    value: 'Policies, procedures, risk assessments and SWMS',
  },
  { capability: 'Photo review', value: 'Photo/site-condition compliance prompts' },
  { capability: 'Access', value: 'Complimate platform and mobile apps' },
  { capability: 'Document management', value: 'Version, review and expiry tracking' },
];

const FAQ_ITEMS = [
  {
    question: 'Is Complimate an AI compliance assistant for Australia?',
    answer:
      'Yes. Complimate is positioned as an Australian WHS and compliance platform, with its AI assistant designed around company compliance documents and Australian WHS requirements.',
  },
  {
    question: 'Can the AI assistant answer questions from company policies?',
    answer:
      'Yes. The assistant can answer questions using uploaded company policies, procedures and registers.',
  },
  {
    question: 'Can Complimate help create a SWMS?',
    answer:
      'The AI assistant provides drafting support for SWMS and risk assessments. Generated content should be reviewed before use.',
  },
  {
    question: 'Does Complimate include document management software?',
    answer:
      'Yes. The platform includes a searchable document hub with folders, version information, review dates, expiry dates and document filtering.',
  },
  {
    question: 'Does the AI assistant replace professional WHS or legal advice?',
    answer:
      'No. AI-generated compliance content should not be treated as legal advice. Businesses should review generated material and obtain appropriate professional advice where required.',
  },
];

const { path: PAGE_PATH, title: PAGE_TITLE, description: PAGE_DESCRIPTION } =
  getPublicSeoPage('/ai-compliance-assistant/');

function CheckIcon() {
  return (
    <span className="seo-check" aria-hidden="true">
      <svg width="16" height="16" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M3.75 9.75L7.125 13.125L14.25 4.875"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

const ICON_PATHS = {
  search: (
    <>
      <circle cx="11" cy="11" r="6" />
      <path d="m16 16 4 4" />
    </>
  ),
  shield: <path d="M12 3 5 6v5c0 4.6 2.9 7.6 7 10 4.1-2.4 7-5.4 7-10V6l-7-3Z" />,
  audit: (
    <>
      <path d="M8 4h8" />
      <path d="M9 3v3h6V3" />
      <path d="M6 5H4v16h16V5h-2" />
      <path d="m8 13 2.2 2.2L16 9.5" />
    </>
  ),
  edit: (
    <>
      <path d="m4 20 4.2-1 10.9-10.9a2.1 2.1 0 0 0-3-3L5.2 16 4 20Z" />
      <path d="m14.8 6.4 3 3" />
    </>
  ),
  photo: (
    <>
      <rect x="3" y="5" width="18" height="15" rx="2" />
      <circle cx="9" cy="10" r="2" />
      <path d="m21 16-5-5L5 20" />
    </>
  ),
  file: (
    <>
      <path d="M6 3h8l4 4v14H6V3Z" />
      <path d="M14 3v5h5" />
      <path d="M9 13h6M9 17h6" />
    </>
  ),
  chat: (
    <>
      <path d="M4 5h16v12H9l-5 4V5Z" />
      <path d="M8 9h8M8 13h5" />
    </>
  ),
  folder: (
    <>
      <path d="M3 6h7l2 2h9v11H3V6Z" />
      <path d="M3 10h18" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="9" r="3" />
      <circle cx="17" cy="10" r="2.5" />
      <path d="M3 20c.5-4 2.5-6 6-6s5.5 2 6 6" />
      <path d="M15 15c3 0 5 1.7 5.5 5" />
    </>
  ),
  spark: (
    <>
      <path d="m12 3 1.3 4.2L17.5 9l-4.2 1.8L12 15l-1.3-4.2L6.5 9l4.2-1.8L12 3Z" />
      <path d="m19 15 .6 1.9 1.9.6-1.9.6L19 21l-.6-1.9-1.9-.6 1.9-.6L19 15Z" />
    </>
  ),
};

function FeatureIcon({ name }) {
  return (
    <span className="ai-feature-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        {ICON_PATHS[name] || ICON_PATHS.spark}
      </svg>
    </span>
  );
}

function AIComplianceAssistant() {
  const [openFaq, setOpenFaq] = useState(0);
  const { mode } = useThemeMode();

  useSeoMeta({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    path: PAGE_PATH,
  });

  return (
    <SeoPageLayout>
      <div className="ai-assistant-page">
      <section className="ai-hero">
        <Container>
          <div className="ai-hero-grid">
            <div className="ai-hero-copy">
              <p className="seo-eyebrow"><FeatureIcon name="spark" /> AI Compliance Assistant</p>
              <h1 className="seo-h1">AI Compliance Assistant</h1>
              <p className="seo-lead">
                Complimate&apos;s AI Compliance Assistant helps Australian
                businesses find answers from their own policies, procedures and
                compliance documents. It can also connect guidance to WHS
                legislation, ISO standards and government requirements, while
                supporting document drafting and photo-based compliance reviews.
                It is designed for WHS managers, compliance officers, supervisors
                and teams that need practical compliance information without
                searching through multiple files.
              </p>
              <div className="seo-cta-row">
                <Button
                  as={Link}
                  to="/contact-us/"
                  className="btn-teal ai-primary-button"
                  id="ai_book_demo_hero"
                >
                  Book a Demo
                  <span aria-hidden="true">→</span>
                </Button>
              </div>
            </div>
            <div className="ai-hero-visual">
              <div className="ai-visual-glow" aria-hidden="true" />
              <div className="ai-product-window">
                <div className="ai-window-bar" aria-hidden="true">
                  <span /><span /><span />
                </div>
                <img
                  src={mode === 'dark' ? DarkChat : LightChat}
                  alt="Complimate AI Compliance Assistant interface"
                  width="800"
                  height="600"
                  fetchPriority="high"
                />
              </div>
            </div>
          </div>
        </Container>
      </section>

      <section className="seo-section ai-intro-section">
        <Container>
          <div className="ai-intro-card">
            <div className="ai-section-marker"><FeatureIcon name="chat" /></div>
            <div>
              <h2 className="seo-h2">What Is an AI Compliance Assistant?</h2>
              <p className="seo-body">
                An AI Compliance Assistant is an AI-powered tool that helps teams
                work with compliance information using natural-language questions.
              </p>
              <p className="seo-body">
                Complimate&apos;s assistant works with documents uploaded by your
                company, including policies, procedures and registers. It can
                answer questions based on those documents and map relevant
                guidance to WHS legislation, ISO standards and government
                requirements.
              </p>
              <p className="seo-body">
                This makes it useful when a worker or manager needs to find a
                specific procedure, understand an audit requirement or review
                existing compliance information.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="seo-section ai-help-section">
        <Container>
          <div className="seo-section-header ai-centered-heading">
            <h2 className="seo-h2">
              How Can Complimate AI Help With WHS Compliance?
            </h2>
            <p className="seo-body">
              Complimate combines AI assistance with its wider{' '}
              <Link
                to={APP_URLS.WHS_COMPLIANCE_SOFTWARE}
                className="section-description-link"
              >
                WHS compliance software
              </Link>
              {' '}platform. Common uses include:
            </p>
          </div>
          <div className="ai-capability-grid">
            {HELP_USES.map((item, index) => (
              <article className="ai-capability-card" key={item.title}>
                <FeatureIcon name={['search', 'shield', 'audit', 'edit', 'photo', 'file', 'chat'][index]} />
                <h3 className="seo-h4">{item.title}</h3>
                <p className="seo-body">— {item.text}</p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section className="seo-section seo-section-alt ai-documents-section">
        <Container>
          <div className="ai-documents-grid">
            <div className="ai-document-visual" aria-hidden="true">
              <div className="ai-document-stack">
                <div className="ai-document-sheet ai-sheet-back" />
                <div className="ai-document-sheet ai-sheet-middle" />
                <div className="ai-document-sheet ai-sheet-front">
                  <FeatureIcon name="folder" />
                  <span className="ai-sheet-line ai-sheet-line-lg" />
                  <span className="ai-sheet-line" />
                  <span className="ai-sheet-line ai-sheet-line-sm" />
                  <div className="ai-sheet-checks">
                    <CheckIcon /><span />
                    <CheckIcon /><span />
                    <CheckIcon /><span />
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h2 className="seo-h2">
                Can AI Compliance Software Work With Your Existing Documents?
              </h2>
              <p className="seo-body">
                Yes. Complimate&apos;s Document Management Software provides a
                central document hub for policies, procedures, SWMS, WHS plans,
                registers and compliance records.
              </p>
              <p className="seo-body">
                Documents can be organised into folders and managed with names,
                versions, review dates and expiry dates. The system also provides
                search and filters for expired or soon-to-expire documents.
              </p>
              <p className="seo-body">
                This gives the AI assistant a practical document base to work from
                while helping your team keep compliance records organised.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="seo-section">
        <Container>
          <div className="seo-section-header ai-centered-heading">
            <h2 className="seo-h2">
              What Can You Use the AI Compliance Assistant For?
            </h2>
          </div>
          <div className="ai-use-case-grid">
            {USE_CASES.map((item, index) => (
              <article className="ai-use-case-card" key={item.title}>
                <div className="ai-card-topline">
                  <FeatureIcon name={['search', 'audit', 'edit', 'photo'][index]} />
                  <span>0{index + 1}</span>
                </div>
                <h3 className="seo-h3">{item.title}</h3>
                {item.paragraphs.map((paragraph, paragraphIndex) => (
                  <p key={`${item.title}-${paragraphIndex}`} className="seo-body">
                    {paragraph}
                  </p>
                ))}
                {item.quote && (
                  <p className="seo-quote">
                    &quot;{item.quote}&quot;
                  </p>
                )}
                {item.afterQuote && (
                  <p className="seo-body">{item.afterQuote}</p>
                )}
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section className="seo-section ai-cta-section">
        <Container>
          <div className="ai-cta-card">
            <div className="ai-cta-orb ai-cta-orb-one" aria-hidden="true" />
            <div className="ai-cta-orb ai-cta-orb-two" aria-hidden="true" />
            <div className="ai-cta-copy">
              <h2 className="seo-h2">Start Managing Compliance With AI</h2>
              <p className="seo-body">
                Use Complimate to connect your compliance documents, AI assistance
                and day-to-day WHS workflows in one platform.
              </p>
            </div>
            <Button
              as={Link}
              to="/contact-us/"
              className="btn-teal ai-cta-button"
              id="ai_book_demo_mid"
            >
              Book a Demo
              <span aria-hidden="true">→</span>
            </Button>
          </div>
        </Container>
      </section>

      <section className="seo-section ai-features-section">
        <Container>
          <div className="seo-section-header ai-centered-heading">
            <h2 className="seo-h2">
              What Document Management Features Are Included?
            </h2>
            <p className="seo-body">
              Complimate&apos;s document management functionality includes:
            </p>
          </div>
          <span className="visually-hidden">Feature</span>
          <span className="visually-hidden">What it helps you do</span>
          <div className="ai-feature-list" role="list">
            {DOC_FEATURES.map((row) => (
              <div className="ai-feature-row" role="listitem" key={row.feature}>
                <CheckIcon />
                <div>
                  <h3 className="seo-h4">{row.feature}</h3>
                  <p className="seo-body">{row.helps}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="seo-body seo-diff-note">
            These capabilities are part of Complimate&apos;s document management
            module.
          </p>
        </Container>
      </section>

      <section className="seo-section seo-section-alt ai-audience-section">
        <Container>
          <div className="ai-audience-card">
            <div className="ai-audience-icon"><FeatureIcon name="users" /></div>
            <div>
              <h2 className="seo-h2">Who Is Complimate AI For?</h2>
              <p className="seo-body">
                Complimate is designed for Australian organisations managing
                workplace health and safety and compliance activities. Its stated
                target industries include construction, childcare, health,
                not-for-profit organisations and local government. Typical users
                include WHS/HSE managers, compliance officers, quality managers,
                site supervisors, operations managers and consultants.
              </p>
              <p className="seo-body">
                It may be less suitable for businesses looking only for a
                general-purpose AI chatbot with no connection to their compliance
                documents or workflows.
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="seo-section ai-specifications-section">
        <Container>
          <div className="seo-section-header ai-centered-heading">
            <h2 className="seo-h2">AI Compliance Assistant Specifications</h2>
          </div>
          <div className="seo-table-wrap ai-spec-table">
            <table className="seo-table">
              <thead>
                <tr>
                  <th scope="col">Specification</th>
                  <th scope="col">Complimate</th>
                </tr>
              </thead>
              <tbody>
                {SPEC_TABLE.map((row) => (
                  <tr key={row.capability}>
                    <th scope="row">{row.capability}</th>
                    <td>{row.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="seo-body seo-diff-note">
            The functionality above is based on the current Complimate product
            brief.
          </p>
        </Container>
      </section>

      <section className="seo-section seo-section-alt ai-faq-section" id="faq">
        <Container>
          <div className="seo-section-header text-center">
            <h2 className="seo-h2 seo-faq-heading">Frequently Asked Questions</h2>
          </div>
          <div className="seo-faq-list">
            {FAQ_ITEMS.map((item, index) => {
              const isOpen = openFaq === index;
              return (
                <div
                  key={item.question}
                  className={`seo-faq-item ${isOpen ? 'open' : ''}`}
                >
                  <button
                    type="button"
                    className="seo-faq-question"
                    aria-expanded={isOpen}
                    onClick={() => setOpenFaq(isOpen ? -1 : index)}
                  >
                    <h3 className="seo-h5">{item.question}</h3>
                    <span className="seo-faq-icon" aria-hidden="true">
                      {isOpen ? '−' : '+'}
                    </span>
                  </button>
                  {isOpen && (
                    <p className="seo-faq-answer seo-body">{item.answer}</p>
                  )}
                </div>
              );
            })}
          </div>
        </Container>
      </section>
      </div>
    </SeoPageLayout>
  );
}

export default AIComplianceAssistant;
